import { FormEvent, useEffect, useState } from "react";
import { format } from "date-fns";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ApiErrorMessages from "../components/UI/ApiErrorMessages";
import Button from "../components/UI/Button";
import ErrorMessages from "../components/UI/ErrorMessages";
import FileUpload from "../components/UI/FileUpload";
import Input from "../components/UI/Input";
import Loader from "../components/UI/Loader";
import Modal from "../components/UI/Modal";
import * as fileScanStatus from "../constants/fileScanStatus";
import * as fileUploadStatus from "../constants/fileUploadStatus";
import UploadableFile from "../models/pageModels/UploadableFile";
import {
    PublicSubmissionFollowUp as FollowUp,
    PublicSubmissionFollowUpIdentity,
    PublicSubmissionFollowUpPublicView
} from "../models/serviceModels/PublicSubmissionFollowUp";
import {
    getPublicSubmissionFollowUp,
    getPublicSubmissionFollowUpIdentityRequirements,
    submitPublicSubmissionFollowUpResponse
} from "../services/formsService";
import '../components/PublicSubmissions/PublicSubmissionDetailTabs.css';
import './PublicSubmissionFollowUp.css';

const MAX_NOTE_LENGTH = 10000;

const normalizeIdentity = (identity: PublicSubmissionFollowUpIdentity): PublicSubmissionFollowUpIdentity => ({
    firstName: identity.firstName.trim().toLowerCase(),
    lastName: identity.lastName.trim().toLowerCase(),
    dateOfBirth: identity.dateOfBirth.trim()
});

const mapFiles = (followUp: Pick<FollowUp, 'files'>): UploadableFile[] => followUp.files.map(file => ({
    fileId: file.fileId,
    name: file.name,
    created: file.created,
    status: file.status === fileScanStatus.CLEAN ? fileUploadStatus.UPLOADED :
        file.status === fileScanStatus.INFECTED ? fileUploadStatus.INFECTED :
            fileUploadStatus.SCANNING
}));

const PublicSubmissionFollowUp = () => {
    const { token: routeToken = '' } = useParams();
    const token = routeToken.trim();
    const location = useLocation();
    const navigate = useNavigate();
    const navigationState = location.state as { followUpSubmitted?: boolean } | null;
    const arrivedAfterSubmission = navigationState?.followUpSubmitted === true;
    const [view, setView] = useState<PublicSubmissionFollowUpPublicView>();
    const [files, setFiles] = useState<UploadableFile[]>([]);
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [confirmSubmit, setConfirmSubmit] = useState(false);
    const [apiError, setApiError] = useState<any>();
    const [errors, setErrors] = useState<string[]>([]);
    const [unavailable, setUnavailable] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [identity, setIdentity] = useState<PublicSubmissionFollowUpIdentity>({
        firstName: '',
        lastName: '',
        dateOfBirth: ''
    });
    const [identityVerifiedForToken, setIdentityVerifiedForToken] = useState('');
    const [identityError, setIdentityError] = useState('');
    const [dateOfBirthRequired, setDateOfBirthRequired] = useState<boolean>();

    useEffect(() => {
        document.title = 'Follow-up Documentation';
        const existingRobots = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
        const robots = existingRobots || document.createElement('meta');
        const originalContent = existingRobots?.content;
        robots.name = 'robots';
        robots.content = 'noindex, nofollow, noarchive';
        if (!existingRobots) document.head.appendChild(robots);

        return () => {
            if (!existingRobots) robots.remove();
            else robots.content = originalContent || '';
        };
    }, []);

    useEffect(() => {
        setIdentity({ firstName: '', lastName: '', dateOfBirth: '' });
        setIdentityError('');
        setDateOfBirthRequired(undefined);
    }, [token]);

    const applyView = (result: PublicSubmissionFollowUpPublicView) => {
        setView(result);
        setFiles(mapFiles(result.currentFollowUp));
        setNote('');
    };

    useEffect(() => {
        let active = true;

        setView(undefined);
        setFiles([]);
        setNote('');
        setApiError(undefined);
        setErrors([]);
        setConfirmSubmit(false);
        setUnavailable(false);
        setSubmitted(false);

        if (!token) {
            setSubmitted(arrivedAfterSubmission);
            setUnavailable(!arrivedAfterSubmission);
            setLoading(false);
            return () => { active = false; };
        }

        setLoading(true);
        const request = identityVerifiedForToken === token
            ? getPublicSubmissionFollowUp(token, normalizeIdentity(identity))
            : getPublicSubmissionFollowUpIdentityRequirements(token);

        request.then(result => {
            if (!active) return;
            if ('dateOfBirthRequired' in result) {
                setDateOfBirthRequired(result.dateOfBirthRequired);
                return;
            }

            applyView(result);
        }).catch(() => {
            if (!active) return;
            console.error('Unable to load public submission follow-up');
            setUnavailable(true);
        }).finally(() => {
            if (active) setLoading(false);
        });

        return () => { active = false; };
    }, [token, arrivedAfterSubmission, identityVerifiedForToken]);

    const submitIdentity = (event: FormEvent) => {
        event.preventDefault();
        const normalizedIdentity = normalizeIdentity(identity);
        if (!normalizedIdentity.firstName || !normalizedIdentity.lastName || (dateOfBirthRequired && !normalizedIdentity.dateOfBirth)) {
            setIdentityError(dateOfBirthRequired
                ? 'Enter your first name, last name, and date of birth.'
                : 'Enter your first name and last name.');
            return;
        }

        setIdentity(normalizedIdentity);
        setIdentityError('');
        setIdentityVerifiedForToken(token);
    };

    const validate = () => {
        const nextErrors: string[] = [];
        const hasCleanFile = files.some(file => file.status === fileUploadStatus.UPLOADED && file.fileId);
        if (!note.trim() && !hasCleanFile) nextErrors.push('Add a note, at least one file, or both.');
        if (files.some(file => file.status !== fileUploadStatus.UPLOADED)) {
            nextErrors.push('Wait for every file to finish scanning and remove any infected or failed files.');
        }
        setErrors(nextErrors);
        return nextErrors.length === 0;
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        setApiError(undefined);
        if (validate()) setConfirmSubmit(true);
    };

    const submitResponse = () => {
        if (!token || !view?.currentFollowUp.id || !validate()) return;

        setSubmitting(true);
        setApiError(undefined);
        submitPublicSubmissionFollowUpResponse(
            token,
            note.trim(),
            files.filter(file => file.status === fileUploadStatus.UPLOADED && file.fileId).map(file => file.fileId!)
        ).then(() => {
            navigate('/forms/public/submissions/follow-up', {
                replace: true,
                state: { followUpSubmitted: true }
            });
            setView(undefined);
            setFiles([]);
            setNote('');
            setConfirmSubmit(false);
            setSubmitted(true);
        }).catch(error => {
            setApiError(error);
            setConfirmSubmit(false);
        }).finally(() => setSubmitting(false));
    };

    if (loading) {
        return <section id="main-content" tabIndex={-1} className="public-follow-up-page"><Loader /></section>;
    }

    if (submitted) {
        return <section id="main-content" tabIndex={-1} className="public-follow-up-page">
            <div className="public-follow-up-message" role="status">
                <h1>Documentation submitted</h1>
                <p>Your documentation was sent successfully. This link has expired, and this page can now be closed.</p>
            </div>
        </section>;
    }

    if (identityVerifiedForToken !== token) {
        return <section id="main-content" tabIndex={-1} className="public-follow-up-page">
            <div className="container">
                <div className="row justify-content-center margin-bottom-5">
                    <div className="column-10 public-follow-up-card">
                        <h1>Verify your identity</h1>
                        <p>Enter the information used for this form to continue.</p>
                        <form onSubmit={submitIdentity} className="public-follow-up-response">
                            <ErrorMessages messages={identityError ? [identityError] : []} />
                            <Input id="follow-up-first-name" label="First name" value={identity.firstName} required={true}
                                onChange={firstName => setIdentity(current => ({ ...current, firstName }))} />
                            <Input id="follow-up-last-name" label="Last name" value={identity.lastName} required={true}
                                onChange={lastName => setIdentity(current => ({ ...current, lastName }))} />
                            {dateOfBirthRequired && <Input id="follow-up-date-of-birth" type="date" label="Date of birth" value={identity.dateOfBirth} required={true}
                                onChange={dateOfBirth => setIdentity(current => ({ ...current, dateOfBirth }))} />}
                            <div className="row justify-content-end margin-top-5">
                                <div className="column-auto"><Button id="verify-follow-up-identity" type="submit" text="Continue" /></div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>;
    }

    if (unavailable || !view) {
        return <section id="main-content" tabIndex={-1} className="public-follow-up-page">
            <div className="public-follow-up-message" role="alert">
                <h1>Follow-up unavailable</h1>
                <p>This follow-up link is invalid or no longer available. Contact the organization that shared it with you.</p>
            </div>
        </section>;
    }

    const activeFollowUp = view.currentFollowUp;
    const history = [
        {
            id: 'original',
            date: view.submissionDate,
            activity: 'Initial public submission',
            status: 'Received'
        },
        ...view.history.map(followUp => ({
            id: followUp.id,
            date: followUp.submitted || followUp.cancelled || followUp.created,
            activity: `Follow-up documentation ${followUp.sequence}`,
            status: followUp.status === 'OPEN' ? 'Requested' : followUp.status === 'SUBMITTED' ? 'Submitted' : 'Cancelled'
        }))
    ].sort((left, right) => new Date(left.date).getTime() - new Date(right.date).getTime());

    return <section id="main-content" tabIndex={-1} className="public-follow-up-page">
        <div className="container">
            <div className="row justify-content-center margin-bottom-5">
                <div className="column-10 public-follow-up-card">
                    <h1>{view.formName} Follow-up</h1>
                    <div className="public-follow-up-organization">Requested by {view.organizationName}</div>
                    <dl className="public-follow-up-participant-summary margin-top-5">
                        <div>
                            <dt>Name</dt>
                            <dd>{view.participant.firstName}</dd>
                        </div>
                        <div>
                            <dt>Initial submission received</dt>
                            <dd>{format(new Date(view.submissionDate), 'P')}</dd>
                        </div>
                        <div>
                            <dt>Prior follow-up submissions</dt>
                            <dd>{view.priorSubmissionCount}</dd>
                        </div>
                    </dl>

                    <div className="public-submission-follow-up-panel margin-top-5">
                        <div className="row justify-content-between align-items-start">
                            <div className="column">
                                <div className="text-bold">Requested {format(new Date(activeFollowUp.created), 'Pp')}</div>
                            </div>
                            <div className="column-auto">
                                <span className="follow-up-status open">Awaiting response</span>
                            </div>
                        </div>
                        <section className="public-follow-up-request" aria-labelledby="current-follow-up-request-heading">
                            <h2 id="current-follow-up-request-heading">Documentation requested</h2>
                            <div className="public-follow-up-request-text">{activeFollowUp.requestText}</div>
                        </section>

                        <form onSubmit={handleSubmit} className="public-follow-up-response">
                            <h2>Provide follow-up documentation</h2>
                            <ErrorMessages messages={errors} />
                            <ApiErrorMessages error={apiError} />
                            <Input
                                id="follow-up-response-note"
                                type="multi"
                                label="Notes"
                                value={note}
                                maxLength={MAX_NOTE_LENGTH}
                                onChange={setNote}
                                placeholder="Add any notes for the organization" />
                            <div className="margin-top-5">
                                <FileUpload
                                    id="follow-up-response-files"
                                    label="Files"
                                    multiple={true}
                                    value={files}
                                    onChange={setFiles}
                                    publicFollowUpToken={token} />
                            </div>
                            <div className="row justify-content-end margin-top-5">
                                <div className="column-auto">
                                    <Button
                                        id="submit-follow-up-response"
                                        type="submit"
                                        text="Submit Documentation"
                                        disabled={submitting || files.some(file => file.status !== fileUploadStatus.UPLOADED)} />
                                </div>
                            </div>
                        </form>
                    </div>

                    <section className="public-follow-up-history margin-top-5" aria-labelledby="public-follow-up-history-heading">
                        <h2 id="public-follow-up-history-heading">Submission history</h2>
                        <div className="table-scroll">
                            <table>
                                <thead><tr><th scope="col">Date</th><th scope="col">Activity</th><th scope="col">Status</th></tr></thead>
                                <tbody>{history.map(item => <tr key={item.id}>
                                    <td>{format(new Date(item.date), 'P')}</td>
                                    <td>{item.activity}</td>
                                    <td>{item.status}</td>
                                </tr>)}</tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </div>
            <div className="row justify-content-center margin-vertical-5">
                <div className="column-auto">
                    <span className="text-sub">Powered by <a href="https://www.countbubble.com" target="_blank" rel="noreferrer">countbubble</a></span>
                </div>
            </div>
        </div>
        <Modal
            id="confirm-follow-up-response"
            visible={confirmSubmit}
            className="small"
            title="Submit Documentation"
            onClose={() => !submitting && setConfirmSubmit(false)}>
            <p>Your response cannot be changed after it is submitted. Are you ready to continue?</p>
            <div className="row justify-content-between margin-top-5">
                <div className="column-auto">
                    <Button id="cancel-follow-up-response" text="Go Back" className="secondary" disabled={submitting} onClick={() => setConfirmSubmit(false)} />
                </div>
                <div className="column-auto">
                    <Button id="confirm-follow-up-response-submit" text="Submit" loading={submitting} disabled={submitting} onClick={submitResponse} />
                </div>
            </div>
        </Modal>
    </section>;
};

export default PublicSubmissionFollowUp;
